/**
 * Choosing who you are, on the home screen, under the map.
 *
 * The in-game picker exists and is a good panel, but it is *in* the game:
 * you have to start a world, look at yourself, decide you would rather be
 * somebody else, and open a panel. Which map and which person are the same
 * kind of question - "what am I about to play" - and they belong together.
 *
 * It writes the same `SKIN_KEY` the in-game picker does and nothing else, so
 * there is one answer to "who am I" and no order of operations to get wrong:
 * the game is built after this screen, and `loadSavedSkin()` reads whatever
 * was chosen here.
 *
 * The portraits are the same paper dolls at a smaller scale, because a list
 * of five names is a list of five words and a козак is not a word - and
 * they are folded away until asked for, because five of them is a lot of a
 * two-hundred-pixel column to spend on a question most people answer once.
 * The row above them says who is chosen without opening anything.
 */

import { PICKABLE_SKINS, type SkinDef } from "../core/player/skins.js";
import { previewCanvas, SKIN_KEY } from "./skinPicker.js";
import type { Lang } from "../core/text/lang.js";

/**
 * Small enough that five of them fit the menu column.
 *
 * The column is 216 pixels and a paper doll is about twenty across, so at
 * two they come out three and two, which is what they should do: one row of
 * five would be five thumbnails nobody can tell apart, and at three the
 * strip pushed «Почати гру» off the bottom of an eight-hundred-pixel screen.
 */
const STRIP_SCALE = 2;

/** Whether the strip was left open. A convenience, like the choice itself. */
const OPEN_KEY = "vyzhyvannia.skinOpen";

export class SkinStrip {
  private readonly cards = new Map<string, HTMLElement>();
  private current: SkinDef;

  /** Fired when a different person is chosen. */
  onSelect: ((def: SkinDef) => void) | null = null;

  constructor(
    /** The row this fills. Handed in, so the style guard can see its id. */
    private readonly row: HTMLElement,
    /** The line above it, which names the choice and opens the rest. */
    head: HTMLElement,
    private readonly nameEl: HTMLElement,
    private readonly caret: HTMLElement,
    initial: SkinDef,
  ) {
    this.current = initial;
    let open = false;
    try {
      open = localStorage.getItem(OPEN_KEY) === "1";
    } catch {
      // Private windows and blocked site data both throw here. Folded, then.
    }
    this.fold(open);
    head.addEventListener("click", (e) => {
      // The overlay behind this turns any click into "start playing".
      e.stopPropagation();
      this.fold(this.row.hidden);
    });
    for (const def of PICKABLE_SKINS) {
      const card = document.createElement("button");
      card.className = "skin-pick";
      card.type = "button";
      card.title = def.name;
      card.appendChild(previewCanvas(def, STRIP_SCALE));
      card.addEventListener("click", (e) => {
        /*
         * The overlay behind this turns any click into "start playing".
         *
         * Every other row on this screen stops its own clicks for the same
         * reason - reaching for a setting must not drop you into a world.
         */
        e.stopPropagation();
        this.select(def);
      });
      this.row.appendChild(card);
      this.cards.set(def.id, card);
    }
    this.row.addEventListener("click", (e) => e.stopPropagation());
    this.mark();
  }

  /**
   * Show the people, or put them away, and remember which.
   *
   * The attribute and nothing else: every browser's own stylesheet makes
   * `[hidden]` `display: none !important`, so no rule of ours is needed and
   * one written here did nothing for a day.
   */
  private fold(open: boolean): void {
    this.row.hidden = !open;
    // A triangle rather than a word: it is the same mark every folding thing
    // on every screen uses, and it needs no translating.
    this.caret.textContent = open ? "▾" : "▸";
    try {
      localStorage.setItem(OPEN_KEY, open ? "1" : "0");
    } catch {
      // Not being able to remember it is not worth failing over.
    }
  }

  private mark(): void {
    for (const [id, card] of this.cards) {
      card.classList.toggle("chosen", id === this.current.id);
    }
    this.say();
  }

  /** Say who is chosen, in words, because a thumbnail is not a name. */
  private say(): void {
    this.nameEl.textContent = this.lang === "en"
      ? this.current.en : this.current.name;
  }

  lang: Lang = "uk";

  /** Called by applyLang: the name under the strip is in the game's language. */
  relabel(lang: Lang): void {
    this.lang = lang;
    for (const def of PICKABLE_SKINS) {
      const card = this.cards.get(def.id);
      if (card !== undefined) card.title = lang === "en" ? def.en : def.name;
    }
    this.say();
  }

  select(def: SkinDef): void {
    this.current = def;
    this.mark();
    try {
      localStorage.setItem(SKIN_KEY, def.id);
    } catch {
      // Private windows and blocked site data both throw here. Not being
      // able to remember the choice is not worth failing over.
    }
    this.onSelect?.(def);
  }

  /**
   * Show that somebody else chose for us, without choosing again.
   *
   * The in-game picker and this strip are the same choice seen twice, and
   * each tells the other. Without a way to be *told*, the telling would be a
   * loop: strip selects, picker selects, strip selects.
   */
  showChosen(def: SkinDef): void {
    this.current = def;
    this.mark();
  }

  get selected(): SkinDef {
    return this.current;
  }
}
